import React, { ReactElement, useMemo } from 'react';
import CheckmarkIcon from '../../assets/icons/CheckmarkIcon';
import CloseIcon from '../../assets/icons/actions/CloseIcon';
import useTheme from '../../hooks/ui/useTheme';
import { DeliveryProvider, ORDER_FULFILLMENT_STATUSES, ORDER_METHODS } from '../../services/exports/Constants';
import { Order, OrderFulfillmentEvent } from '../../services/exports/Interfaces';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';

interface Props {
  order: Order | null;
  className?: string;
}

interface EventProps {
  icon: ReactElement;
  title: null | string;
  description: null | string;
  extra?: string;
  className?: string;
}

export default function OrderTimeline(props: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Order:OrderTimeline' });

  const { order, className } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const theme = useTheme();
  const { formatTime } = useAppMethods();

  function renderIcon(event: Partial<OrderFulfillmentEvent>) {
    const color =
      {
        [ORDER_FULFILLMENT_STATUSES.REJECTED]: theme.color.danger,
      }[event.status] ?? theme.color.brand.primary;

    const icon = {
      [ORDER_FULFILLMENT_STATUSES.REJECTED]: <CloseIcon color={theme.color.white['100']} />,
    }[event.status] ?? <CheckmarkIcon color={theme.color.white['100']} />;

    return (
      <div className={iconContainer} style={{ backgroundColor: color }}>
        {icon}
      </div>
    );
  }

  function renderEventExtra(event: Partial<OrderFulfillmentEvent>) {
    return (
      {
        [ORDER_FULFILLMENT_STATUSES.CREATED]: <p className="my-auto text-sm">#{order.fulfillment.number}</p>,
        [ORDER_FULFILLMENT_STATUSES.ACCEPTED]: (
          <p className="my-auto text-sm">{formatTime(order.fulfillment.estimated_pickup_time)}</p>
        ),
      }[event.status] ?? <p className="my-auto text-sm">{formatTime(event.created_at)}</p>
    );
  }

  const Event = ({ icon, title, description, extra, className }: EventProps) =>
    Boolean(title) && (
      <div className={classnames('flex flex-row justify-between', className)}>
        {icon}
        <div className="ml-sm flex flex-col flex-1 my-auto">
          <div className="flex justify-between">
            <p className="text-sm my-auto">{title}</p>
            {extra}
          </div>
          {Boolean(description) && <p className="text-gray-700 text-sm mt-2xs">{description}</p>}
        </div>
      </div>
    );

  const renderWaitingOrderConfirmation = () => {
    if (![ORDER_FULFILLMENT_STATUSES.CREATED, ORDER_FULFILLMENT_STATUSES.REVIEWED].includes(order.fulfillment.status)) {
      return null;
    }

    return (
      <Event
        icon={
          <div className={iconContainer} style={{ backgroundColor: theme.color.gray['100'] }}>
            <CheckmarkIcon color={theme.color.gray['300']} />
          </div>
        }
        title={t('waiting_confirmation.title')}
        description={t('waiting_confirmation.description')}
        className="mt-md"
      />
    );
  };

  const renderPickupTimeChangedLabel = useMemo(() => {
    if (
      !order.fulfillment.initial_confirmation_time ||
      !order.fulfillment.estimated_pickup_time ||
      (order.method === ORDER_METHODS.DELIVERY && order.delivery_provider !== DeliveryProvider.Standalone) ||
      order.fulfillment.initial_confirmation_time === order.fulfillment.estimated_pickup_time
    ) {
      return null;
    }

    return (
      <div className="w-fit mb-md p-sm bg-notice rounded">
        <p className="text-sm">
          {t('warning_label.pickup_time_changed', {
            company: company?.name,
            initialTime: formatTime(order.fulfillment.initial_confirmation_time),
            currentTime: formatTime(order.fulfillment.estimated_pickup_time),
          })}
        </p>
      </div>
    );
  }, [order.fulfillment.initial_confirmation_time, order.fulfillment.estimated_pickup_time]);

  return (
    <div className={classnames('w-full p-md rounded-2xl border', className)}>
      {renderPickupTimeChangedLabel}
      {order.fulfillment.events.map((event: OrderFulfillmentEvent, index: number) => (
        <Event
          icon={renderIcon(event)}
          title={t(`status.${event.status}.title`, { defaultValue: null })}
          description={t(`status.${event.status}.description`, { defaultValue: null })}
          extra={renderEventExtra(event)}
          key={`order-event-${event.status}`}
          className={classnames({ 'mb-md': index !== order.fulfillment.events.length - 1 })}
        />
      ))}
      {renderWaitingOrderConfirmation()}
    </div>
  );
}

const iconContainer = 'w-8 h-8 rounded-full flex justify-center items-center my-auto';
